import Phaser from 'phaser';

/**
 * =============================================================================
 * Auto-snap de posición para TODO el texto (y contenedores) del juego
 * =============================================================================
 *
 * Problema: la pixel-font se ve borrosa / con shimmer cuando su posición cae en un valor
 * NO entero (sub-píxel). La config global ya usa `roundPixels: true`, pero no alcanza: el
 * texto con `resolution: 2` y los contenedores anclados en coords fraccionarias igual se ven
 * mal, y el redondeo manual (`Math.round`) estaba aplicado de forma inconsistente por
 * call-site.
 *
 * Solución: subclases de Text/BitmapText/Container que **redondean su propia x/y**, y override
 * de los factories de la Scene (`scene.add.text` / `add.bitmapText` / `add.container`) para que
 * los devuelvan por defecto. Así TODO —labels de entidades, HUD, texto flotante e inclusive las
 * ventanas/paneles— queda en posición entera sin tocar cada call-site, y el código futuro lo
 * hereda gratis.
 *
 * Cómo: en Phaser 3.90 `x`/`y` son props planas del mixin Transform. Redefinimos `x`/`y` como
 * accessors en el prototipo de cada subclase con `Object.defineProperty` (evita el conflicto
 * TS property↔accessor de hacerlo con `get x()` en la clase). El setter redondea; así se atrapa
 * `setPosition` (que asigna `this.x`/`this.y`), la asignación directa y los **tweens** (que
 * animan `.x`/`.y` sin pasar por `setPosition`, ej. los números de daño). SÓLO afecta a
 * texto/containers: sprites/imágenes NO se tocan (su interpolación suave queda intacta).
 *
 * El registro es global al módulo Phaser (una vez, al importar este archivo). `main.ts` lo
 * importa por side-effect ANTES de crear cualquier `Phaser.Game`, así cubre también las escenas
 * de editores/login (inofensivo: redondear posiciones de texto es deseable en todos lados).
 */

/** Backing fields que crean los accessors instalados abajo. */
interface SnapBacking {
  _snapX?: number;
  _snapY?: number;
}

/** Redefine `x`/`y` en un prototipo para que redondeen al asignarse. */
function installSnappedXY(proto: object): void {
  Object.defineProperty(proto, 'x', {
    configurable: true,
    get(this: SnapBacking): number {
      return this._snapX ?? 0;
    },
    set(this: SnapBacking, value: number): void {
      this._snapX = Math.round(value);
    },
  });
  Object.defineProperty(proto, 'y', {
    configurable: true,
    get(this: SnapBacking): number {
      return this._snapY ?? 0;
    },
    set(this: SnapBacking, value: number): void {
      this._snapY = Math.round(value);
    },
  });
}

export class SnapText extends Phaser.GameObjects.Text {}
export class SnapBitmapText extends Phaser.GameObjects.BitmapText {}
export class SnapContainer extends Phaser.GameObjects.Container {}

installSnappedXY(SnapText.prototype);
installSnappedXY(SnapBitmapText.prototype);
installSnappedXY(SnapContainer.prototype);

// Override de los factories de la Scene (mismo shape que los nativos: sólo displayList.add).
const factory = Phaser.GameObjects.GameObjectFactory;

factory.register(
  'text',
  function (
    this: Phaser.GameObjects.GameObjectFactory,
    x: number,
    y: number,
    text: string | string[],
    style?: Phaser.Types.GameObjects.Text.TextStyle
  ): Phaser.GameObjects.Text {
    return this.displayList.add(new SnapText(this.scene, x, y, text, style)) as Phaser.GameObjects.Text;
  }
);

factory.register(
  'bitmapText',
  function (
    this: Phaser.GameObjects.GameObjectFactory,
    x: number,
    y: number,
    font: string,
    text?: string | string[],
    size?: number,
    align?: number
  ): Phaser.GameObjects.BitmapText {
    return this.displayList.add(
      new SnapBitmapText(this.scene, x, y, font, text, size, align)
    ) as Phaser.GameObjects.BitmapText;
  }
);

factory.register(
  'container',
  function (
    this: Phaser.GameObjects.GameObjectFactory,
    x?: number,
    y?: number,
    children?: Phaser.GameObjects.GameObject | Phaser.GameObjects.GameObject[]
  ): Phaser.GameObjects.Container {
    return this.displayList.add(
      new SnapContainer(this.scene, x, y, children)
    ) as Phaser.GameObjects.Container;
  }
);
